import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { Box } from '@mui/material'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import UsaAPI from 'api/faber/UsaAPI'
import { AxiosError } from "axios";
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import ColorBox from 'common/Box/ColorWhiteBox'
import NormalText from 'common/Typography/NormalText'
import { FaberResponse } from "helpers/backendTypes";
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { ViewProfileButton } from 'pages/Customers/sharedComponents'
import { SearchToolbar } from 'pages/OTPacks/sharedComponents'
import { GetAllGlobalUsersDetails } from 'types/api/faber/globalCustomers.type'

export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export default function FaberGlobalUserSearch() {
  const navigate = useNavigate()

  const [rows, setRows] = useState<GetAllGlobalUsersDetails[]>([])
  const [statusChecked] = useState<string[]>([])
  const [bundleChecked] = useState<string[]>([])

  const [tempSearchValue, setTempSearchValue] = useState<string>('')
  // hard code the start date to before launch date
  const [startDate] = useState<Date>(new Date(2022, 9, 1))
  const [endDate] = useState<Date>(changeMonth(new Date(), +3))
  const [sortDate, setSortDate] = useState<string>('DESC')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')

  const [pageOffset, setPageOffset] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  // TODO: TO UNCOMMENT WHEN API RETURNS CORRECT FIELDS FOR PAGE 2 ONWARDS

  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = [
    'Email',
    'Name',
    'User ID',
    'ICCID',
  ]

  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize,
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      filterStartDate: startDate,
      filterEndDate: endDate,
      sortDate,
    }),
    [
      pageOffset,
      pageSize,
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      startDate,
      endDate,
      sortDate,
    ]
  )

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPageOffset(tmpOffset / 10)
    setPage(newPage)
  }

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel[0].sort === 'asc') {
        setSortDate('ASC')
      } else {
        setSortDate('DESC')
      }
    },
    []
  )

  const { data: getAllGlobalUsers, isLoading: isGetAllUsersLoading } = useQuery(
    [
      'getAllGlobalUsers',
      [queryOptions.searchValue, queryOptions.searchCriteria ]
    ],
    async () =>
      queryOptions.searchValue.length > 0
        ? UsaAPI.getAllGlobalUsers({
            faberToken: faberToken,
            searchCriteria: queryOptions.searchCriteria,
            searchValue: queryOptions.searchValue,
          })
        : null,
    {
      onSuccess: (res) => {
        return res
      },
      onError: (e : AxiosError<FaberResponse>) => {
        // Create a toast showing error
        toast.error(e.response?.data.message)
        setRows([])
      }
    }
  )

  useEffect(() => {
    if (getAllGlobalUsers) {
      const { users } = getAllGlobalUsers.data.data
      const searchedUsers: GetAllGlobalUsersDetails[] = users.map(
        ({ name, family_name, given_name, user_id, user_metadata }) => ({
          name,
          family_name,
          given_name,
          user_id,
          user_metadata,
          login_method: user_id
        })
      )
      setRows(searchedUsers)
    }
  }, [getAllGlobalUsers])

  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempSearchValue(e.target.value)
  }

  const onSearchSubmit = () => {
    const trimmedValue = tempSearchValue.trim();
  
    const hasInvalidCharsEmail = /[^a-zA-Z0-9@._+-]/.test(trimmedValue);
    const hasInvalidCharsName = /[^a-zA-Z0-9 ._-]/.test(trimmedValue)

    // ^ only allow letters, numbers, @, ., _, +, -
   if (
  (searchCriteria === "Email" && hasInvalidCharsEmail) ||
  (searchCriteria === "Name" && hasInvalidCharsName)
  ) {
    toast.error("Search value contains invalid characters.");
    return;
  }
  
    setSearchValue(trimmedValue);
    resetPage();
  };
  
  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  const columns: GridColDef[] = [
    {
      field: 'family_name',
      headerName: 'Name',
      width: 120,
      renderCell: (params: { row: GetAllGlobalUsersDetails }) => {
        return <NormalText>{params.row.name}</NormalText>
      }
    },
    { field: 'user_id', headerName: 'UserId', width: 150 },
    {
      field: 'email',
      headerName: 'Email',
      width: 150,
      renderCell: (params: { row: GetAllGlobalUsersDetails }) => {
        return <NormalText>{params.row.user_metadata?.email}</NormalText>
      }
    },
    {
      field: 'login_method',
      headerName: 'Login Method',
      width: 120,
      renderCell: (params: { row: GetAllGlobalUsersDetails }) => {
        const prefix = params.row.user_id.split('|')[0]

        if (prefix.includes('-oauth2')) {
          const modifiedPrefix = prefix.replace('-oauth2', '')
          return (
            <NormalText>{capitalizeFirstLetter(modifiedPrefix)}</NormalText>
          )
        } else if (prefix.includes('apple')) {
          const modifiedPrefix = prefix.replace('apple', 'Apple Id')
          return <NormalText>{modifiedPrefix}</NormalText>
        } else {
          return <NormalText>{capitalizeFirstLetter(prefix)}</NormalText>
        }
      }
    },
    {
      field: 'viewProfile',
      flex: 0.75,
      minWidth: 90,
      filterable: false,
      sortable: false,
      headerName: 'View Profile',
      renderCell: (params: { row: GetAllGlobalUsersDetails }) => {
        return <ViewProfileButton customerRow={params.row} />
      }
    }
  ]

  return (
    <Box>
      <ColorBox>
        <Box>
          <NormalText isBold>
            Please type customer details in the search bar to begin
          </NormalText>
          <MUIDataGrid
            getRowId={(row) => `${row.iccid}-${rows.indexOf(row)}`}
            tableHeight='750px'
            rows={rows}
            pagination
            columns={columns}
            paginationMode='server'
            // rowCount={rowCount}
            page={page}
            loading={isGetAllUsersLoading}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            disableColumnMenu={true}
            sortingMode='server'
            sortingOrder={['desc', 'asc']}
            onSortModelChange={handleSortModelChange}
            onRowDoubleClick={(params) => {
              {
                navigate(`view-profile/${params.row.user_id}`, {
                  state: params.row
                })
              }
            }}
            components={{
              Toolbar: SearchToolbar
            }}
            componentsProps={{
              toolbar: {
                searchBar: 'server',
                serverSearchBarProps: {
                  onSubmit: onSearchSubmit,
                  onChange: onSearchValueChange,
                  options: searchOptions,
                  defaultValue: searchCriteria,
                  handleChange: handleSearchCriteriaChange
                }
              }
            }}
          />
        </Box>
      </ColorBox>
    </Box>
  )
}

export interface StatsData {
  labels: string[]
  datasets: {
    label: string
    data: number[]
    borderColor?: string
    backgroundColor?: string
  }[]
}
